/**
 * Auth Provider - Logto-basierte Authentifizierung
 */

import { ReactNode, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLogto } from '@/auth/oidc'
import { Loader2 } from 'lucide-react'

import { useAuthStore } from '@/store/authStore'
import { useModuleStore } from '@/store/moduleStore'
import { api, setTokenProvider } from '@/services/api'

interface AuthProviderProps {
  children: ReactNode
}

function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">Laden...</p>
      </div>
    </div>
  )
}

const PUBLIC_ROUTES = ['/callback', '/login', '/setup']
const PUBLIC_ROUTE_PREFIXES = ['/ring/']
const ERP_EXTENSION_CONTEXT_REQUEST = 'netzfabriks.erp.contextRequest'
const ERP_EXTENSION_CONTEXT_RESPONSE = 'netzfabriks.erp.contextResponse'

export function AuthProvider({ children }: AuthProviderProps) {
  const [isReady, setIsReady] = useState(false)
  const initRef = useRef(false)
  const location = useLocation()
  const { isAuthenticated: logtoAuth, getAccessToken, isLoading: logtoLoading } = useLogto()
  const { setUser, setTenants, setAuthenticated, isAuthenticated: storeAuth } = useAuthStore()

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname)
    || PUBLIC_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))

  // Wire up token provider for api.ts
  useEffect(() => {
    setTokenProvider(async () => {
      try {
        const token = await getAccessToken()
        return token ?? undefined
      } catch {
        return undefined
      }
    })
  }, [getAccessToken])

  useEffect(() => {
    const handleExtensionContextRequest = async (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return
      if (event.data?.type !== ERP_EXTENSION_CONTEXT_REQUEST) return

      const requestId = event.data.requestId

      if (!logtoAuth) {
        window.postMessage({
          type: ERP_EXTENSION_CONTEXT_RESPONSE,
          requestId,
          ok: false,
          error: 'Bitte zuerst in NetzERP anmelden.',
        }, window.location.origin)
        return
      }

      try {
        const token = await getAccessToken()
        const authState = useAuthStore.getState()
        if (!token) {
          throw new Error('Kein ERP-Zugriffstoken verfuegbar.')
        }

        // Ticket-Kontext ist optional: Das Capture Studio funktioniert auch ohne
        // offenes Ticket (neues Ticket aus Snapshot). ticketId/ticketUrl nur, wenn
        // man gerade auf einer Ticket-Detailseite /tickets/<id> ist.
        const ticketMatch = window.location.pathname.match(/^\/tickets\/([^/?#]+)/)
        const ticketId = ticketMatch?.[1] || null

        window.postMessage({
          type: ERP_EXTENSION_CONTEXT_RESPONSE,
          requestId,
          ok: true,
          context: {
            appOrigin: window.location.origin,
            ticketId,
            ticketUrl: ticketId ? window.location.href : null,
            token,
            tenantId: authState.tenantId,
            userEmail: authState.user?.email || null,
            userName: authState.user?.firstName && authState.user?.lastName
              ? `${authState.user.firstName} ${authState.user.lastName}`
              : authState.user?.email || null,
          },
        }, window.location.origin)
      } catch (error: any) {
        window.postMessage({
          type: ERP_EXTENSION_CONTEXT_RESPONSE,
          requestId,
          ok: false,
          error: error?.message || 'ERP-Ticket-Kontext konnte nicht gelesen werden.',
        }, window.location.origin)
      }
    }

    window.addEventListener('message', handleExtensionContextRequest)
    return () => window.removeEventListener('message', handleExtensionContextRequest)
  }, [getAccessToken, logtoAuth])

  // Sync: wenn Logto sagt "nicht mehr eingeloggt", Store leeren
  useEffect(() => {
    if (!logtoLoading && !logtoAuth && storeAuth) {
      useAuthStore.getState().logout()
    }
  }, [logtoLoading, logtoAuth, storeAuth])

  // Initialize auth state ONCE when Logto is done loading
  useEffect(() => {
    if (logtoLoading || isPublicRoute) return
    if (initRef.current) return
    initRef.current = true

    const init = async () => {
      if (!logtoAuth) {
        setIsReady(true)
        return
      }

      try {
        const user = await api.auth.getMe()
        setUser(user)
        setAuthenticated(true)

        try {
          const tenants = await api.auth.getMyTenants()
          setTenants(tenants)
        } catch {
          // OK
        }

        // Module-Status laden
        try {
          const modules = await api.modules.getEnabled()
          useModuleStore.getState().setEnabledModules(modules)
        } catch {
          // Fallback: alle Module als aktiv behandeln
        }
      } catch (err) {
        console.error('Failed to load user profile:', err)
      } finally {
        setIsReady(true)
      }
    }

    init()
  }, [logtoLoading, logtoAuth, isPublicRoute])

  if (isPublicRoute) {
    return <>{children}</>
  }

  // NUR vor der initialen Initialisierung Loading zeigen.
  // NICHT bei spaeteren Token-Refreshes, da sonst alle Kinder
  // unmounten und eine Endlosschleife aus Remount+API-Call entsteht.
  if (!isReady) {
    return <AuthLoading />
  }

  return <>{children}</>
}

/**
 * Hook zum Abrufen des aktuellen Benutzers
 */
export function useCurrentUser() {
  const navigate = useNavigate()
  const { signOut } = useLogto()
  const { user, isAuthenticated } = useAuthStore()

  const logout = async () => {
    try {
      await signOut(window.location.origin + '/login')
      // signOut redirectet den Browser - Code danach wird nicht erreicht
    } catch {
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }
  }

  return {
    isAuthenticated,
    user,
    login: () => navigate('/login'),
    logout,
    profile: user
      ? {
          name: user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.email,
          email: user.email,
          picture: undefined,
        }
      : null,
  }
}

export default AuthProvider
